import { Button } from '@advanza/shadcn-ui/src/components/ui/button'
import { Label } from '@advanza/shadcn-ui/src/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@advanza/shadcn-ui/src/components/ui/select'
import { LoadingDots } from '@advanza/ui'
import clsx from 'clsx'
import type { FilterAnswer, Question } from './types'

interface FilterPanelProps {
    questions: Question[]
    selectedAnswers: FilterAnswer[]
    onChangeAnswers: (answers: FilterAnswer[]) => void
    isLoading: boolean
    disabled?: boolean
}

const FilterPanel = ({
    questions,
    selectedAnswers,
    onChangeAnswers,
    isLoading,
    disabled,
}: FilterPanelProps) => {
    if (isLoading) {
        return (
            <div className="py-2">
                <LoadingDots />
            </div>
        )
    }

    if (questions.length === 0) {
        return null
    }

    const getSelectedOption = (questionId: number): string => {
        const answer = selectedAnswers.find((a) => a.questionId === questionId)
        return answer?.options[0]?.toString() || ''
    }

    const handleOptionChange = (questionId: number, optionId: string) => {
        const current = getSelectedOption(questionId)
        const newAnswers = selectedAnswers.filter((a) => a.questionId !== questionId)

        // Toggle off if same value selected
        if (optionId !== '' && optionId !== current) {
            newAnswers.push({ questionId, options: [Number(optionId)], isFilter: true })
        }

        onChangeAnswers(newAnswers)
    }

    const handleClearAll = () => {
        onChangeAnswers([])
    }

    return (
        <div className={clsx('space-y-2', disabled && 'pointer-events-none opacity-50')}>
            <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
                {questions.map((question) => (
                    <div key={question.question_id} className="space-y-1">
                        <Label className="text-sm text-muted-foreground">
                            {question.question_name_filter || question.question}
                        </Label>
                        <Select
                            value={getSelectedOption(question.question_id)}
                            onValueChange={(val) => handleOptionChange(question.question_id, val)}>
                            <SelectTrigger className="h-8 bg-white text-sm">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                {question.options.map((option) => (
                                    <SelectItem
                                        key={option.option_id}
                                        value={option.option_id.toString()}>
                                        {option.value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ))}
                {selectedAnswers.length > 0 && (
                    <div className="flex items-end">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-sm"
                            onClick={handleClearAll}
                            disabled={disabled}>
                            Clear
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default FilterPanel
